import {
  erkenneVerb,
  supportedCommands,
  type CatalogEntry,
  type Erkennung,
} from '@spirit-testing/testoffice-ng-engine-core/catalog';

export type TcdlTokenKind = 'command' | 'object' | 'string' | 'variable' | 'comment' | 'text';
export type TcdlCommandStatus = 'known' | 'not-implemented' | 'unknown';

export interface TcdlToken {
  readonly kind: TcdlTokenKind;
  readonly text: string;
  readonly status?: TcdlCommandStatus;
}

export interface TcdlCommandMatch {
  /** Offset of the command in the line, after leading whitespace. */
  readonly start: number;
  readonly length: number;
  readonly entry?: CatalogEntry;
  readonly status: TcdlCommandStatus;
}

const ARGUMENT_PIECE = /"[^"]*"?|\{[^}]*\}?|[,\s]+|[^,"{\s](?:[^,"{]*[^,"{\s])?/gu;
const VARIABLE_IN_STRING = /(\{[^}"]*\}?)/u;
// Same cut as extractTarget in the engine parser: a condition behind the object is not part of its name.
const CONDITION = /\s*\b(?:WENN|IF\s+VALUE)\b.*$/iu;
const SUPPORTED = new Set(supportedCommands());

function statusOf(verb: Erkennung['verb'], entry: CatalogEntry | undefined): TcdlCommandStatus {
  if (verb === 'UNBEKANNT') return 'unknown';
  if (verb === 'NICHT_UMGESETZT' || (entry && !SUPPORTED.has(entry))) return 'not-implemented';
  return 'known';
}

/**
 * The command at the start of a TCDL line, or `null` for blank and comment lines. Uses the
 * parser's own recognition from engine-core, so the editor colors what the run executes.
 */
export function commandAt(text: string): TcdlCommandMatch | null {
  const start = text.length - text.trimStart().length;
  const body = text.slice(start).trimEnd();
  if (!body || body.startsWith('//')) return null;

  const { verb, laenge, eintrag } = erkenneVerb(body);
  // Unknown lines and engine switches have no catalog name; the part before the arguments stands in.
  const length = laenge || /^[^,"{]*/u.exec(body)![0].trimEnd().length;
  return length ? { start, length, entry: eintrag, status: statusOf(verb, eintrag) } : null;
}

/** Splits a TCDL line into colorable parts; joined again they give the line unchanged. */
export function tokenizeLine(text: string): TcdlToken[] {
  if (!text.trim()) return [];
  const tokens: TcdlToken[] = [];
  const push = (kind: TcdlTokenKind, part: string): void => {
    if (!part) return;
    const last = tokens[tokens.length - 1];
    if (last && last.kind === kind && kind !== 'command') {
      tokens[tokens.length - 1] = { kind, text: last.text + part };
    } else {
      tokens.push({ kind, text: part });
    }
  };

  const lead = text.length - text.trimStart().length;
  push('text', text.slice(0, lead));
  const body = text.slice(lead);
  if (body.startsWith('//')) {
    push('comment', body);
    return tokens;
  }

  const command = commandAt(text);
  let rest = body;
  let objectKind: TcdlTokenKind = 'object';
  if (command) {
    tokens.push({ kind: 'command', text: body.slice(0, command.length), status: command.status });
    rest = body.slice(command.length);
    if (command.entry && !command.entry.grammar.includes('Object')) objectKind = 'text';
  }

  for (const [piece] of rest.matchAll(ARGUMENT_PIECE)) {
    if (piece.startsWith('"')) {
      piece.split(VARIABLE_IN_STRING).forEach((part, i) => push(i % 2 === 1 ? 'variable' : 'string', part));
    } else if (piece.startsWith('{')) {
      push('variable', piece);
    } else if (/^[,\s]/u.test(piece)) {
      push('text', piece);
    } else {
      push(objectKind, piece);
    }
  }
  return tokens;
}

export interface TcdlRange {
  /** Column in the line. */
  readonly start: number;
  readonly length: number;
  readonly text: string;
}

export interface TcdlObjectRef {
  readonly start: number;
  readonly length: number;
  readonly library: TcdlRange;
  readonly entry: TcdlRange;
}

/**
 * The `Library.Entry` references of a line, split at the first dot like the backend's
 * ObjectReferenceResolver. Selectors (`css=…`), parts glued to a `{variable}` and names
 * without a dot are left out: they cannot be checked against a library.
 */
export function objectReferences(text: string, tokens: readonly TcdlToken[] = tokenizeLine(text)): TcdlObjectRef[] {
  const refs: TcdlObjectRef[] = [];
  let start = 0;
  tokens.forEach((token, i) => {
    const at = start;
    start += token.text.length;
    if (token.kind !== 'object') return;
    if (tokens[i - 1]?.kind === 'variable' || tokens[i + 1]?.kind === 'variable') return;
    const name = token.text.replace(CONDITION, '');
    const dot = name.indexOf('.');
    const library = name.slice(0, dot);
    const entry = name.slice(dot + 1);
    if (dot <= 0 || !entry || library.includes('=')) return;
    refs.push({
      start: at,
      length: name.length,
      library: { start: at, length: dot, text: library },
      entry: { start: at + dot + 1, length: entry.length, text: entry },
    });
  });
  return refs;
}
